/**
 * Timeline feature module — the core project-load + playback/board verbs:
 * setSqlitePath (the single most load-bearing message — project load), updateShot,
 * setActiveScene, selectShot, seek, sceneReady, thumbnailChanged, boardFillState,
 * allScenesReady. Case bodies moved verbatim from embeddedBridge.ts.
 *
 * The `onProjectLoaded` one-shot callback lives here as module-private state:
 * initBridge sets it via setOnProjectLoaded(onReady ?? null); the setSqlitePath
 * handler fires-and-nulls it once the project finishes loading.
 */

import i18next from 'i18next';
import type { HandlerMap } from '../dispatch';
import { sendToNative } from '../transport';
import { bufferSceneReady, flushSceneReadyBuffer } from '@/store/sceneReadyBuffer.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import type { BoardFillState } from '@/store/projectStore.ts';
import { recordSceneReady, emitLoadPerf } from '@/perf/loadPerf.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useTelemetryStore } from '@/telemetry/telemetryStore';
import { trackEvent } from '@/telemetry/trackEvent';
import { setEmbeddedSqlitePath } from '@/previs/sqlReader.ts';
import { useSceneSettingsStore } from '@/store/sceneSettingsStore';
// 20260914 — STATIC on purpose, unlike the guide-arming block's dynamic store imports below.
// importChecklistStore imports nothing but zustand (no uiStore, no bridge), so it cannot
// reintroduce the store↔bridge cycle those dynamic imports exist to avoid — and arming
// synchronously with the load keeps the card's first paint in the same commit as the board's.
import { useImportChecklistStore } from '@/store/importChecklistStore.ts';
import { invalidateShotFullImage, clearShotFullImageCache } from './fullImage';
// Static is safe for the same reason tutorialStore's is: castHeadshotStore imports nothing but
// zustand, so it cannot reintroduce the store↔bridge cycle the dynamic imports below exist to avoid.
import { useCastHeadshotStore } from '@/store/castHeadshotStore.ts';
import { resetDossierPipelineLatches } from '@/integration/dossierPipeline.ts';
// Static is safe: tutorialStore's only static import is zustand (it reaches the bridge dynamically).
import { useTutorialStore } from '@/store/tutorialStore';

// ─── Message types ───────────────────────────────────────────────────────

export interface UpdateShotMessage {
  type: 'updateShot';
  payload: { shotId: number; duration?: number; notes?: string };
}

export interface SetActiveSceneMessage {
  type: 'setActiveScene';
  payload: { sceneId: number | null };
}

export interface SeekMessage {
  type: 'seek';
  payload: { time: number };
}

export interface SceneReadyMessage {
  type: 'sceneReady';
  /** `videoCleared: true` (20261002): this animatic shot has no playable video right now — clear the
   *  URL the page carries (a URL in the same entry wins). store/tileVideoMerge.ts. */
  payload: { sceneId: number; shots: Array<{ id: number; thumbnailDataUrl?: string; videoObjectUrl?: string; thumbHash?: string; videoHash?: string; videoCleared?: boolean }> };
}

export interface AllScenesReadyMessage {
  type: 'allScenesReady';
  payload: Record<string, never>;
}

export interface ThumbnailChangedMessage {
  type: 'thumbnailChanged';
  payload: { shotId: number; thumbnailDataUrl?: string; thumbHash?: string; videoHash?: string };
}

export interface BoardFillStateMessage {
  type: 'boardFillState';
  payload: BoardFillState;
}

export interface SelectShotMessage {
  type: 'selectShot';
  payload: { shotId: number };
}

export interface SetSqlitePathMessage {
  type: 'setSqlitePath';
  payload: {
    path: string;
    initialSceneId?: number | null;
    focusShotId?: number;
    userInitiated?: boolean;
    project_source?: 'sample' | 'user_project' | 'imported';
    /** True when the welcome-flow "View Example" path auto-opened this
     *  project (i.e. the user did not manually pick it from the project
     *  list). Lets the View-Example→Timeline funnel filter cleanly without
     *  having to correlate by close-in-time timestamps. */
    auto_opened?: boolean;
    /** True when the project just opened is the built-in EXAMPLE project
     *  (SampleProject2.previs). Unity omits the field otherwise, and omits it on the
     *  refresh re-push path too. Arms the example-project guide — every open, no seen
     *  flag (owner ruling): the guide is what the Examples project is FOR. */
    example_project?: boolean;
    /** True when the project just opened is a TUTORIAL copy — EITHER tour's, since Unity mints the
     *  Getting started and Casting copies into the same sample_02_ slot and checks the one source
     *  file name (TutorialProject.previs). Omitted otherwise, and omitted on the refresh re-push
     *  path like its neighbours above. Keeps the first-entry coach marks (Quick Orient, ScriptOrient)
     *  out of the tutorial projects — they are the reminder for the user's OWN project (owner
     *  20260922). Deliberately a PROJECT fact rather than the tour flag: the tour ends with the user
     *  still standing in the copy. */
    tutorial_project?: boolean;
    /** True when this is a lightweight live-board refresh (Add shot/scene):
     *  reload the project data without resetting expanded/active/selected UI
     *  state, and skip the project_opened telemetry (payload omits
     *  project_source). Set only by the Unity RepushProjectToLiveWebView path. */
    refresh?: boolean;
    /** 20261005 - canvas Coverage overview (`?page=canvas-coverage`) only: the highest
     *  `applyCoverageShotEdit` seq Unity had processed when it built this database (0 for none). Tells
     *  the page which of its own writes the pushed rows already contain. Absent on every other route. */
    coverageEditSeq?: number;
    /** 20261005 (spec §12.2) - canvas Coverage overview only, beside `coverageEditSeq`: `[shotId, mm]`
     *  for each of the scene's shots that has a camera, the whole mm its canvas tile shows (the
     *  project's sensor and aspect, not the loader's full-frame `focalLength`). */
    coverageLensMm?: Array<[number, number]>;
  };
}

export interface CanvasEntryFinishedMessage {
  type: 'canvasEntryFinished';
  /** ok:false = Unity could not set the requested scene current, so the 3D canvas never
   *  opened (the board stayed visible). ok:true = the canvas presented; the board webview
   *  is hidden by the time this evaluates (WKWebView keeps running JS while hidden). */
  payload: { ok: boolean };
}

export type TimelineMessage =
  | UpdateShotMessage
  | SetActiveSceneMessage
  | SeekMessage
  | SceneReadyMessage
  | AllScenesReadyMessage
  | ThumbnailChangedMessage
  | BoardFillStateMessage
  | SelectShotMessage
  | SetSqlitePathMessage
  | CanvasEntryFinishedMessage;

// ─── onProjectLoaded handoff ─────────────────────────────────────────────

// One-shot callback fired after the project finishes loading. initBridge sets it
// via setOnProjectLoaded; the setSqlitePath handler fires-and-nulls it below.
let onProjectLoaded: (() => void) | null = null;

/** Set (or clear) the one-shot onProjectLoaded callback. Called by initBridge. */
export function setOnProjectLoaded(fn: (() => void) | null): void {
  onProjectLoaded = fn;
}

// ─── coverageEditSeq handoff (canvas Coverage overview) ──────────────────

// 20261005 - The canvas Coverage overview page (components/CanvasCoverage) registers here for the
// `coverageEditSeq` each of its pushes carries, and the `coverageLensMm` beside it (spec §12.2; passed
// through as sent, the page reads it). A registration rather than an import: the page is a lazy route
// and its write queue must stay out of this module's eager graph.
type CoverageEditSeqListener = (seq: number, lensMm: SetSqlitePathMessage['payload']['coverageLensMm']) => void;
let coverageEditSeqListener: CoverageEditSeqListener | null = null;

/** Install the listener; the returned disposer clears it only while it is still this one. */
export function registerCoverageEditSeqListener(fn: CoverageEditSeqListener): () => void {
  coverageEditSeqListener = fn;
  return () => { if (coverageEditSeqListener === fn) coverageEditSeqListener = null; };
}

// ─── Inbound handlers ────────────────────────────────────────────────────

export const timelineHandlers = {
  setSqlitePath: (msg: SetSqlitePathMessage) => {
    const { initialSceneId, focusShotId, userInitiated } = msg.payload;
    // 20261005 - canvas Coverage overview: hand the push's seq (and the canvas's lens per shot) over
    // NOW, synchronously, before the load below can resolve. The page adopts pushed rows on the
    // store's project change, and by then it must already know which of its own writes this database
    // contains and what mm each tile shows (canvasCoverageSync.ts).
    if (typeof msg.payload.coverageEditSeq === 'number') {
      coverageEditSeqListener?.(msg.payload.coverageEditSeq, msg.payload.coverageLensMm);
    }
    // Keep sqlReader in sync so getEmbeddedDatabase() works for NLE export (Task 9).
    // Static import at top of file — fires synchronously here.
    setEmbeddedSqlitePath(msg.payload.path);
    console.log('[Bridge] setEmbeddedSqlitePath →', msg.payload.path);
    // Scene Settings' sky-swatch cache is keyed by scene id, and scene ids are per-.previs — so a
    // cached render from the previous project would be served for the SAME id in the next one (the
    // embedded WebView swaps the SQLite in place, with no page reload to wipe it). A `refresh`
    // re-push is the same project re-sent, so it keeps its cache, exactly as it keeps its UI state.
    // Same reasoning covers the on-demand full-res cache, which is keyed by shot id alone: a
    // different project's shot 11 must not be served this one's pixels. A refresh re-push is the
    // same project, so it keeps both caches.
    if (msg.payload.refresh !== true) {
      useSceneSettingsStore.setState({ skyPreviews: {} });
      clearShotFullImageCache();
      // 20260902 — and the cast pipeline's per-project latches, for the same reason: its missing-set
      // key carries no project id, so a different project imported from the same script would match
      // it term for term and be silently skipped (reviewer-proved). Static import, synchronous: the
      // reset must land before the load below completes and the board effect runs the pipeline.
      resetDossierPipelineLatches();
      // 20260922 — and the Cast list's headshot map, for the third time the same reason: its keys
      // are `characters.characterid`, which are per-.previs, so the next project's character 12
      // would be served this one's face until Unity's own `setCharacterHeadshots` arrived. That
      // message always follows this push (never precedes it), so clearing here can never wipe a set
      // that belongs to the incoming project. A refresh re-push is the same document and keeps its
      // portraits, exactly as it keeps the two caches above.
      useCastHeadshotStore.getState().clear();
      // 20260921 (review) — and the tutorial's finale ENCORE. The card outlives the tour on purpose
      // (tutorialStore.finaleEncore), so something has to end it when the tutorial project goes
      // away: the shell's Home button sends closePanel only, and nothing in that path tells this
      // store the document changed. A FRESH load is the authoritative "a different document is on
      // its way" bit; a refresh re-push (the one that follows a sketch apply) keeps the encore, and
      // the tutorial's own open is followed by start(), which clears it anyway. Synchronous, so it
      // lands before the new project renders. TutorialRail's title + scene check stays as a second
      // line of defence. (20260921, fix pass: belt-and-braces rather than a live mechanism — see
      // the stale-tour note below for why no store state actually crosses a project close here.)
      useTutorialStore.getState().clearFinaleEncore();
      // 20260922 (owner) — and the DOCUMENT's own identity: is this a tutorial copy? A fresh load is
      // the one moment the document changes, so this is where the flag is written — including the
      // false that a later ordinary project needs, which is why it is an unconditional write of
      // `=== true` rather than a set-when-present. A refresh re-push is the SAME document (Unity
      // omits the field there, exactly as it omits project_source/example_project), so it keeps
      // whatever this load established. The first-entry coach marks read it through
      // selectInTutorialProject. Static import, synchronous, same as the clear above.
      useTutorialStore.getState().setInTutorialProject(msg.payload.tutorial_project === true);
      // 20261009 (owner) — and is it the bundled EXAMPLE project? Same contract as the line above:
      // written on every fresh load from `example_project` (already on the wire — it arms the
      // example guide below), left alone on a refresh. The Example is a New-user-guide project,
      // minted fresh by Unity on every open, so the first-entry coach marks keep out of it too.
      useTutorialStore.getState().setInExampleProject(msg.payload.example_project === true);
      // 20260921 (design §2.1, fix pass) — THE STALE TOUR: asked and answered, and the answer is
      // that there is nothing to stand down. The spec's §2.1 asks us to confirm how a Home-button
      // exit mid-tour is handled and to add a SILENT local reset here only if `active` can survive
      // into the next ordinary project. On this host it cannot: it is the PANEL that is pooled, not
      // the page. Every non-refresh setSqlitePath is preceded by a WebView teardown and recreate —
      // closePanel → PerformClose → Close() → Exit() → CleanupWebView() destroys the web view
      // (TimelineWebViewPanel.cs, whose own comment there reads "the page is about to be
      // destroyed"), the two scene reloads and the blank-screen recreate each CleanupWebView()
      // before OpenWebView(), and PushProjectToWebView calls a fresh open "a new page and a new
      // bridge session". SceneBoardPage.resolveImportOpen says the same from this side: "the close
      // is what ends it, by destroying this page". So this handler only ever sees a tour that was
      // armed on THIS page, which means a stale-tour reset would be unreachable code — and an
      // actively harmful one to leave lying around: it could not be a bare `active: false` write
      // (that skips end()'s glow sweep, stranding the gold Enhance ring on a real project) and it
      // could not call end() either (that sends `tutorialEnded`, which would clear Unity's
      // tutorialModeActive in the window before it pushes `startTutorial` for a tutorial open).
      // If the board page is ever made to outlive a project close, the stand-down belongs in
      // tutorialStore as an action beside end(), not as a hand-written field set here.
    }
    import('@/bridge/sqliteBridge').then(({ loadProjectFromSqlite }) => {
      loadProjectFromSqlite(msg.payload.path, { preserveUiState: msg.payload.refresh === true }).then(() => {
        if (initialSceneId != null) {
          const store = useProjectStore.getState();
          const project = store.project;
          // 20261005: the canvas side panel's two pages — Script Lining and the Coverage overview —
          // both show ONE scene, the canvas's own.
          const page = new URLSearchParams(window.location.search).get('page');
          const isCanvasScenePanel = page === 'screenplay' || page === 'canvas-coverage';
          if (isCanvasScenePanel) {
            // Screenplay overlay: filter to the active scene so ScreenplayPage
            // shows the correct scene's script (loadProject resets activeSceneId
            // to null, so this must happen after the load completes).
            store.setActiveScene(initialSceneId);
          } else if (project) {
            // Timeline: stay in "All Scenes" view but position at the initial scene
            let offset = 0;
            for (const scene of project.scenes) {
              if (scene.id === initialSceneId) break;
              offset += scene.shots.reduce((sum, s) => sum + s.duration, 0);
            }
            const scene = project.scenes.find(s => s.id === initialSceneId);
            const firstShot = scene?.shots[0];
            if (firstShot) store.selectShot(firstShot.id);
            usePlaybackStore.getState().seek(offset);
          }
        }
        if (focusShotId != null && focusShotId >= 0) {
          const store = useProjectStore.getState();
          store.selectShot(focusShotId, userInitiated ?? false);
          const startTime = store.getShotStartTime(focusShotId);
          if (startTime >= 0) usePlaybackStore.getState().seek(startTime);
        }
        // Signal native that the project is loaded and ready for
        // project-dependent messages (sceneReady, allScenesReady, etc.)
        sendToNative('projectLoaded', {});
        onProjectLoaded?.();
        onProjectLoaded = null;
        // Telemetry: record project source + auto_opened flag and fire
        // project_opened. auto_opened is true only on the welcome-flow
        // "View Example" detour; manual project taps leave it false (and
        // we still emit it explicitly so the funnel filter is unambiguous).
        if (msg.payload.project_source) {
          const autoOpened = msg.payload.auto_opened === true;
          useTelemetryStore.getState().setProjectSource(msg.payload.project_source);
          useTelemetryStore.getState().setAutoOpened(autoOpened);
          trackEvent('project_opened', {
            project_source: msg.payload.project_source,
            auto_opened: autoOpened,
          });
        }
        // ── Guided flows (T12) ───────────────────────────────────────────────
        // Guarded on the WHOLE block, not just the end() below: a `refresh` re-push is the same
        // project re-sent mid-session (the post-apply repush of an in-project screenplay import
        // is one), so it must neither end the guide it just started nor start a new one. Unity
        // omits both arming fields on that path; this guard means a regression there is inert
        // rather than a guide restarting under the user.
        //
        // Dynamic import for the usual reason every handler here uses one — a static store import
        // from a bridge feature module reintroduces the store↔bridge cycle.
        // The tutorialStore bindings destructured below SHADOW the static ones at the top of this
        // file. Deliberate: this block is the pre-existing dynamic-import idiom and stays as it is;
        // the selector simply travels with the store it belongs to.
        if (msg.payload.refresh !== true) {
          Promise.all([
            import('@/store/guideStore'),
            import('@/store/tutorialStore'),
            import('@/store/uiStore'),
          ]).then(([{ useGuideStore }, { useTutorialStore, selectAnyTourActive }, { useUiStore }]) => {
            const guides = useGuideStore.getState();
            // A different project never inherits the previous one's guide.
            guides.end('projectChanged');
            const route = useUiStore.getState().workspaceRoute;
            if (msg.payload.example_project === true) {
              // Not while the bundled TutorialProject's tour owns the screen — that flow is
              // choreographed across native round trips and cannot share the dock.
              // 20260921 — ANY TOUR (design §2.1): the dock is one slot, so either script owns it.
              // Read LIVE rather than snapshotted on arrival: this block runs once the load has
              // resolved, and the tutorial project's own open pushes `startTutorial` in exactly
              // that window — a tour armed between arrival and here must still refuse.
              if (selectAnyTourActive(useTutorialStore.getState())) return;
              guides.start('exampleProject', { skipFirstStep: route === 'timeline' });
            }
            // `project_source: 'imported'` used to arm the first-import guide here. That guide was
            // retired 20260904 in favour of the Script tab's own coach marks, which arm from the
            // component on first tab entry. The payload FIELD stays — telemetry above reads it.
          }).catch(err => {
            console.error('[Bridge] Guide arming failed:', err);
          });
        }
        // ── Script Import Checklist (20260914, owner) ────────────────────────
        // ARMED ON THE LOAD, never on the completion message that precedes it, because the card's
        // shape — V1, or V2 with the "Review changes from prior revision" row — is computed from
        // `project.baseline`, and the store only holds the NEW baseline once this load has resolved.
        // (The reimport reference doc names that window between `reimportComplete` and the repush;
        // arming here is what closes it.) Two arming inputs, both already on the wire, so Unity
        // needed no change for any of this:
        //   · project_source: 'imported' — an initial import, which arrives as a fresh open;
        //   · the reimportComplete latch — an in-project revision, whose repush is a `refresh`.
        //
        // DELIBERATELY OUTSIDE the guided-flows guard above: the revision case IS a refresh
        // re-push, so gating this on `refresh !== true` would arm nothing for the one case the
        // latch exists to serve.
        //
        // TWO LOADS, TWO RULES, and they do not overlap:
        //
        //  · FRESH (refresh !== true) — a different project, or this one re-opened. It ENDS a
        //    checklist the previous project left up (the pooled-panel case, where no page reload
        //    intervenes; end() no-ops when nothing is running, so the common open costs one read)
        //    and DROPS any latch still standing from an abandoned reimport, which must never be
        //    inherited by an unrelated project. It arms on `project_source: 'imported'` alone.
        //  · REFRESH — the same project re-sent mid-session. It must not end a checklist the user
        //    is working through, and the LATCH is its only arming input.
        //
        // The latch consume is version-gated (20260914, reviewer). A refresh repush can already be
        // in flight when `reimportComplete` sets the latch — the cast-bodies-swap repush after a
        // fresh open is the routine one — and an ungated consume was spent by that repush, which
        // carries the PRE-apply baseline: a V1-shaped run, and nothing left to arm when the real
        // post-apply repush landed. Comparing the loaded baseline against the armed one leaves the
        // latch standing for the load that actually moved it.
        const checklist = () => useImportChecklistStore.getState();
        // `?? 0` here, `?? 1` for the row set below, and the difference is deliberate: this is an
        // IDENTITY (does it differ from the armed version?), where "no baseline" is its own value
        // and must not collide with a real version 1; that one is a COUNT, where a host that wrote
        // no baseline should read as the first import rather than as a revision.
        const loadedVersion = useProjectStore.getState().project?.baseline?.versionNumber ?? 0;
        let arm: boolean;
        if (msg.payload.refresh !== true) {
          checklist().end('projectChanged');
          checklist().clearArmOnNextLoad();
          arm = msg.payload.project_source === 'imported';
        } else {
          arm = checklist().consumeArmOnNextLoad(loadedVersion);
        }
        if (arm) {
          // `version_number` is count+1, written by Unity into screenplay_import_baseline. A FIRST
          // import into a blank project lands on 1 and correctly gets no revision row, even when it
          // travelled the reimport machinery; the `?? 1` covers a host that wrote no baseline at all.
          const versionNumber = useProjectStore.getState().project?.baseline?.versionNumber ?? 1;
          checklist().start({ revision: versionNumber >= 2 });
        }
      }).catch(err => {
        console.error('[Bridge] Failed to load SQLite:', err);
      });
    }).catch(err => {
      console.error('[Bridge] Failed to import sqliteBridge:', err);
    });
  },
  updateShot: (msg: UpdateShotMessage) => {
    if (msg.payload.duration != null) {
      useProjectStore.getState().updateShotDuration(msg.payload.shotId, msg.payload.duration, { pushUndo: false });
    }
  },
  setActiveScene: (msg: SetActiveSceneMessage) => {
    useProjectStore.getState().setActiveScene(msg.payload.sceneId);
  },
  selectShot: (msg: SelectShotMessage) => {
    const store = useProjectStore.getState();
    store.selectShot(msg.payload.shotId);
    const startTime = store.getShotStartTime(msg.payload.shotId);
    if (startTime >= 0) usePlaybackStore.getState().seek(startTime);
  },
  seek: (msg: SeekMessage) => {
    usePlaybackStore.getState().seek(msg.payload.time);
  },
  sceneReady: (msg: SceneReadyMessage) => {
    // Buffered, not applied — Unity pushes one of these per scene in a single frame, and applying
    // each one on arrival is the board's post-reload freeze. See store/sceneReadyBuffer.ts for
    // the flush points; every other inbound verb drains it first, so ordering is preserved.
    const t0 = performance.now();
    bufferSceneReady(msg.payload.sceneId, msg.payload.shots);
    recordSceneReady(t0);
  },
  thumbnailChanged: (msg: ThumbnailChangedMessage) => {
    // This poke is Unity's "these pixels changed" signal (a drop, an AI apply, a re-render), and it
    // is the ONLY one — Unity does not rewrite the on-demand {shotId}_full extraction when a shot's
    // image is replaced, so a cached full-res URL would keep painting the old picture behind a
    // freshly-swapped tile. Evicted before the store write, and unconditionally: an empty poke
    // (regen failed) still means the underlying blob moved on. bridge/features/fullImage.ts note 4.
    invalidateShotFullImage(msg.payload.shotId);
    if (msg.payload.thumbnailDataUrl) {
      useProjectStore.getState().markShotThumbnail(msg.payload.shotId, msg.payload.thumbnailDataUrl, {
        thumbHash: msg.payload.thumbHash,
        videoHash: msg.payload.videoHash,
      });
    }
  },
  boardFillState: (msg: BoardFillStateMessage) => {
    useProjectStore.getState().setBoardFillState(msg.payload);
  },
  allScenesReady: (_msg: AllScenesReadyMessage) => {
    // The burst's real flush point: everything Unity pushed since projectLoaded lands here, as one
    // store write, BEFORE the viewReady reply below promises the board is painted with it.
    // dispatchMessage already drained the buffer on the way in; this is the explicit statement of
    // the contract, and a no-op when that ran.
    flushSceneReadyBuffer();
    useProjectStore.getState().markAllScenesReady();
    // Always open in "All Scenes" view — the user can filter from there.
    // This also handles WebView persistence: if the WebView survived a
    // close/reopen cycle, activeSceneId from the previous session is cleared.
    useProjectStore.setState({ activeSceneId: null });
    emitLoadPerf();
    // Signal Unity that all scene/thumbnail data has been applied to the store,
    // so the WebView can now be revealed without a flash of missing thumbnails.
    // Deferred by a double rAF so the signal also means LAID OUT: React has committed, the
    // adaptive grid's layout effect has measured, and one paint has composited. Sent from here
    // (store-write time) the reveal uncovers a board still carrying the pre-measurement fallback
    // tracks, which then re-solve on screen.
    // The send stays UNCONDITIONAL — never behind a guard, and never in anything a later message
    // can cancel. Three native consumers hang off this one message (TryReveal, the repush-coalesce
    // latch, blankScreenGuard.MarkContentReady); a swallowed viewReady strands the board behind
    // the 8s RevealTimeout and re-opens the sparse-board "Display Error" false positive.
    // The rAF chain is RACED against a 100ms timer (review 2026-08-18): rAF alone DEADLOCKS the
    // cold open, where the webview is HIDDEN until viewReady itself triggers the reveal
    // (TryReveal) — a hidden WKWebView gets no rendering opportunities, so its rAF callbacks are
    // never serviced (macOS Standalone: no window before the first Show; iOS/Editor: the
    // UniWebView container is born hidden) and every project open would fall to the 8s
    // RevealTimeout. setTimeout is throttled but NOT suspended for hidden pages, so the timer is
    // the fail-open bound; wherever the page is actually visible (return-to-board repush, resize
    // re-push) the rAF chain wins the race and viewReady still means "laid out". The latch keeps
    // the send exactly-once per allScenesReady whichever side fires first.
    let viewReadySent = false;
    const sendViewReadyOnce = () => {
      if (viewReadySent) return;
      viewReadySent = true;
      sendToNative('viewReady', {});
    };
    requestAnimationFrame(() => requestAnimationFrame(sendViewReadyOnce));
    window.setTimeout(sendViewReadyOnce, 100);
  },
  canvasEntryFinished: (msg: CanvasEntryFinishedMessage) => {
    // 20260818 - CANVAS-ENTRY GATE: clears the board's "Loading scene…" scrim
    // (uiStore.canvasEntryBusy, armed by the Edit-in-3D press sites via canvasEntryBusy.ts).
    // ok:true arrives AFTER Unity hides the board webview, so the board can never repaint
    // scrim-free while still visible. ok:false = the scene could not be opened and the board
    // stayed up — surface a toast; the native ConfirmationPopupPanel that used to fire on
    // that path was an invisible modal UNDER the webview with an unreachable OK.
    //
    // uiStore is loaded LAZILY on purpose: uiStore statically imports embeddedBridge (a real
    // cycle), and a static import here crashed registerFeatureMaps with a half-evaluated
    // handler map when a test imported timeline.ts first (probe, 2026-08-18). Sibling feature
    // modules (settings.ts, audio.ts) do import useUiStore statically and get away with it
    // under today's import order — this one stays lazy because import order is exactly the
    // thing nothing pins.
    // The one-microtask delay is invisible: for ok:true the webview is already hidden, and
    // for ok:false the toast is async anyway.
    void import('@/store/uiStore.ts')
      .then(({ useUiStore }) => useUiStore.getState().setCanvasEntryBusy(false))
      .catch((e) => console.warn('[bridge] canvasEntryFinished could not clear the scrim', e));
    if (msg.payload?.ok === false) {
      // Dynamic import keeps Toast (a React component module) out of the bridge's eager
      // graph; the catch keeps a failed chunk load visible instead of a silent nothing.
      void import('@/components/Toast.tsx')
        .then(({ showToast }) => showToast(i18next.t('errors:canvasEntryFailed'), 'error'))
        .catch((e) => console.warn('[bridge] canvasEntryFailed toast could not be shown', e));
    }
  },
} satisfies HandlerMap;
